import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import FormInput from '../components/FormInput';
import { toast } from '../components/Toast';
import './MenuManagement.css';

function MenuManagement() {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [formData, setFormData] = useState({
    key: '',
    label: '',
    path: '',
    icon: '',
    parentId: null,
    sortOrder: 1,
    active: true
  });

  useEffect(() => {
    fetchMenus();
  }, []);

  const fetchMenus = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/menus');
      const menuData = response.data.data || response.data;
      setMenus(menuData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching menus:', error);
      toast.error('Failed to fetch menus');
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingMenu(null);
    setFormData({
      key: '',
      label: '',
      path: '',
      icon: '',
      parentId: null,
      sortOrder: menus.length + 1,
      active: true
    });
    setShowModal(true);
  };

  const handleEdit = (menu) => {
    setEditingMenu(menu);
    setFormData({
      key: menu.key,
      label: menu.label,
      path: menu.path,
      icon: menu.icon || '',
      parentId: menu.parentId,
      sortOrder: menu.sortOrder,
      active: menu.active
    });
    setShowModal(true);
  };

  const handleDelete = async (menuId) => {
    if (!window.confirm('Are you sure you want to delete this menu?')) {
      return;
    }

    try {
      await axios.delete(`/menus/${menuId}`);
      toast.success('Menu deleted successfully');
      fetchMenus();
    } catch (error) {
      console.error('Error deleting menu:', error);
      toast.error(error.response?.data?.message || 'Failed to delete menu');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingMenu) {
        await axios.put(`/menus/${editingMenu.id}`, formData);
        toast.success('Menu updated successfully');
      } else {
        await axios.post('/menus', formData);
        toast.success('Menu created successfully');
      }
      setShowModal(false);
      fetchMenus();
    } catch (error) {
      console.error('Error saving menu:', error);
      toast.error(error.response?.data?.message || 'Failed to save menu');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const columns = [
    { 
      key: 'sortOrder', 
      label: 'Order',
      render: (menu) => <span className="sort-order">{menu.sortOrder}</span>
    },
    { 
      key: 'icon', 
      label: 'Icon',
      render: (menu) => <span className="menu-icon-preview">{menu.icon || '📄'}</span>
    },
    { key: 'label', label: 'Label' },
    { key: 'key', label: 'Key' },
    { key: 'path', label: 'Path' },
    { 
      key: 'active', 
      label: 'Status',
      render: (menu) => (
        <span className={`status-badge ${menu.active ? 'active' : 'inactive'}`}>
          {menu.active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (menu) => (
        <div className="action-buttons">
          <button onClick={() => handleEdit(menu)} className="btn btn-sm btn-primary">
            ✏️ Edit
          </button>
          <button onClick={() => handleDelete(menu.id)} className="btn btn-sm btn-danger">
            🗑️ Delete
          </button>
        </div>
      )
    }
  ];

  if (loading) {
    return <div className="loading">Loading menus...</div>;
  }

  return (
    <div className="menu-management-container">
      <div className="page-header">
        <h1>Menu Management</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          ➕ Add New Menu
        </button>
      </div>

      <div className="info-card">
        <p>
          <strong>ℹ️ Note:</strong> Menus control the navigation items shown to users based on their roles. 
          After creating or modifying menus, assign them to roles in the Roles & Permissions page.
        </p>
      </div>

      <DataTable
        data={menus}
        columns={columns}
        keyField="id"
      />

      {showModal && (
        <Modal
          isOpen={showModal}
          title={editingMenu ? 'Edit Menu' : 'Create New Menu'}
          onClose={() => setShowModal(false)}
        >
          <form onSubmit={handleSubmit} className="menu-form">
            <FormInput
              label="Menu Key"
              name="key"
              value={formData.key}
              onChange={handleInputChange}
              required
              placeholder="e.g., dashboard, projects"
              helpText="Unique identifier for the menu (lowercase, no spaces)"
            />

            <FormInput
              label="Menu Label"
              name="label"
              value={formData.label}
              onChange={handleInputChange}
              required
              placeholder="e.g., Dashboard, Projects"
              helpText="Display name shown in the navigation"
            />

            <FormInput
              label="Path"
              name="path"
              value={formData.path}
              onChange={handleInputChange}
              required
              placeholder="e.g., /, /projects"
              helpText="URL path for the menu item"
            />

            <FormInput
              label="Icon (Emoji)"
              name="icon"
              value={formData.icon}
              onChange={handleInputChange}
              placeholder="e.g., 📊, 🏗️, 💰"
              helpText="Emoji or icon to display (optional)"
            />

            <FormInput
              label="Sort Order"
              name="sortOrder"
              type="number"
              value={formData.sortOrder}
              onChange={handleInputChange}
              required
              min="1"
              helpText="Display order in the menu (lower numbers appear first)"
            />

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                />
                <span>Active (visible to users)</span>
              </label>
            </div>

            <div className="form-actions">
              <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {editingMenu ? 'Update Menu' : 'Create Menu'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default MenuManagement;
